import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Camera, Upload, Loader2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', EVS: '🌿', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' };

export default function OCRUploadPage({ onNavigate }) {
  const { api, user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('Math');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedQ, setExpandedQ] = useState(null);

  const className = user?.class_name || 'Class 8';
  const subjects = ['Math', 'Science', 'English', 'Social Science', 'Hindi', 'EVS'];

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subject', subject);
      formData.append('class_name', className);

      const res = await api.post('/ocr/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="back-from-ocr" onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="ocr-title">Textbook Scanner</h1>
          <p className="text-xs text-slate-500">Upload photo - get study material</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 mb-5 space-y-4">
        {/* File Input */}
        <div className="relative">
          <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="ocr-file" data-testid="ocr-file-input" />
          <label htmlFor="ocr-file"
            className="w-full h-48 border-2 border-dashed border-slate-700 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-violet-500/50 transition-all">
            {preview ? (
              <img src={preview} alt="Preview" className="w-full h-full object-contain rounded-xl" />
            ) : (
              <>
                <Camera className="w-10 h-10 text-slate-600 mb-3" />
                <p className="text-sm text-slate-400">Tap to upload textbook photo</p>
                <p className="text-xs text-slate-600 mt-1">JPG, PNG supported</p>
              </>
            )}
          </label>
        </div>

        {/* Subject */}
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Subject</label>
          <select data-testid="ocr-subject" value={subject} onChange={e => setSubject(e.target.value)}
            className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-10 px-3 text-sm text-slate-100 outline-none appearance-none">
            {subjects.map(s => <option key={s} value={s}>{subjectIcons[s]} {s}</option>)}
          </select>
        </div>

        <button data-testid="ocr-upload-btn" onClick={handleUpload} disabled={!file || uploading}
          className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-12 font-semibold text-sm transition-all glow-violet flex items-center justify-center gap-2 disabled:opacity-50">
          {uploading ? <><Loader2 className="w-5 h-5 animate-spin" /> Scanning & Generating...</> : <><Upload className="w-5 h-5" /> Scan & Generate Study Material</>}
        </button>
      </div>

      {/* Results */}
      {result && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="ocr-results">
          {/* Extracted Text */}
          {result.extracted_text && result.extracted_text !== '[Image uploaded - generating content based on subject context]' && (
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-2">Extracted Text</h3>
              <p className="text-xs text-slate-300 whitespace-pre-wrap max-h-32 overflow-y-auto">{result.extracted_text}</p>
            </div>
          )}

          {/* Summary */}
          {result.study_material?.summary && (
            <div className="bg-violet-500/5 border border-violet-500/20 rounded-2xl p-4">
              <p className="text-sm text-violet-200">{result.study_material.summary}</p>
            </div>
          )}

          {/* Topics */}
          {result.study_material?.extracted_topics?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {result.study_material.extracted_topics.map((t, i) => (
                <span key={i} className="text-xs bg-cyan-500/10 text-cyan-300 px-3 py-1 rounded-full">{t}</span>
              ))}
            </div>
          )}

          {/* Key Concepts */}
          {result.study_material?.key_concepts?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Key Concepts</h3>
              <div className="space-y-2">
                {result.study_material.key_concepts.map((c, i) => (
                  <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-3" data-testid={`ocr-concept-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-3 h-3 text-violet-400" />
                      <span className="text-sm font-medium text-white">{c.title}</span>
                    </div>
                    <p className="text-xs text-slate-400 ml-5">{c.explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Practice Questions */}
          {result.study_material?.practice_questions?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Practice Questions</h3>
              <div className="space-y-2">
                {result.study_material.practice_questions.map((q, i) => (
                  <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden" data-testid={`ocr-q-${i}`}>
                    <button onClick={() => setExpandedQ(expandedQ === i ? null : i)} className="w-full flex items-center justify-between p-3 text-left">
                      <span className="text-sm text-white">Q{i + 1}: {q.question}</span>
                      {expandedQ === i ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                    </button>
                    {expandedQ === i && (
                      <div className="px-3 pb-3 border-t border-slate-800 pt-2">
                        <p className="text-xs text-emerald-400">Answer: {q.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center text-xs text-violet-400 bg-violet-500/5 border border-violet-500/10 rounded-xl py-3">
            +20 XP | +10 Coins earned for scanning textbook!
          </div>
        </motion.div>
      )}
    </div>
  );
}
